import { motion } from "framer-motion";
import { ExternalLink, ArrowUpRight } from "lucide-react";

const JOURNALS = [
  {
    id: "ijmrcr",
    title: "International Journal of Medical Reviews and Case Reports",
    short: "IJMRCR",
    issn: "ISSN: 2534-9821",
    description:
      "A peer-reviewed international medical journal publishing reviews, original articles, case reports, and clinical research across all fields of medicine.",
    url: "https://www.mdpub.net",
    cover:
      "https://images.pexels.com/photos/25626509/pexels-photo-25626509.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    accent: "var(--sci-teal)",
    fields: ["Clinical Reviews", "Case Reports", "Original Research"],
  },
  {
    id: "ijsm",
    title: "International Journal of Surgery and Medicine",
    short: "IJSM",
    issn: "E-ISSN: 2367-699X · P-ISSN: 2367-7414",
    description:
      "An international peer-reviewed journal focused on surgery, perioperative medicine, clinical innovations, and multidisciplinary patient care.",
    url: "https://www.ejos.org",
    cover:
      "https://images.pexels.com/photos/25626516/pexels-photo-25626516.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    accent: "var(--sci-gold)",
    fields: ["Surgery", "Perioperative Medicine", "Clinical Innovation"],
  },
];

export default function Journals() {
  return (
    <section
      id="journals"
      data-testid="journals-section"
      className="relative py-24 md:py-32 bg-white border-y border-[var(--sci-border)]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14 md:mb-20">
          <div>
            <div className="overline text-[var(--sci-teal)] mb-5">
              · 02 / Our Journals
            </div>
            <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl text-[var(--sci-navy)] tracking-tight leading-[1.02] max-w-3xl">
              Two international titles. <br />
              <em className="not-italic text-[var(--sci-teal)]">One editorial standard.</em>
            </h2>
          </div>
          <p className="md:max-w-sm text-[var(--sci-text-muted)] leading-relaxed">
            Both titles are open-access, indexed, and curated by international
            editorial boards spanning clinical and surgical disciplines.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 md:gap-8">
          {JOURNALS.map((j, i) => (
            <motion.a
              key={j.id}
              href={j.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`journal-card-${j.id}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.65, delay: 0.1 * i }}
              className="group relative bg-white border border-[var(--sci-border)] card-lift block overflow-hidden"
            >
              {/* Cover */}
              <div className="relative aspect-[16/10] overflow-hidden">
                <img
                  src={j.cover}
                  alt={`${j.title} cover`}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  loading="lazy"
                />
                <div
                  className="absolute inset-0"
                  style={{
                    background: `linear-gradient(180deg, rgba(11,22,44,0.05) 0%, rgba(11,22,44,0.55) 100%)`,
                  }}
                />
                {/* Top metadata strip */}
                <div className="absolute top-0 left-0 right-0 p-5 md:p-6 flex items-start justify-between text-white">
                  <div>
                    <div className="overline opacity-90">{j.short}</div>
                    <div className="font-mono-sci text-[11px] mt-1.5 tracking-wider opacity-95">
                      {j.issn}
                    </div>
                  </div>
                  <div
                    className="w-10 h-10 flex items-center justify-center border border-white/30 backdrop-blur-md group-hover:bg-white group-hover:text-[var(--sci-navy)] transition-all"
                    style={{ background: "rgba(255,255,255,0.08)" }}
                  >
                    <ArrowUpRight size={16} />
                  </div>
                </div>
                {/* Accent bar */}
                <div
                  className="absolute bottom-0 left-0 h-1 w-0 group-hover:w-full transition-all duration-500"
                  style={{ background: j.accent }}
                />
              </div>

              {/* Body */}
              <div className="p-7 md:p-9">
                <h3 className="font-serif-display text-2xl md:text-3xl text-[var(--sci-navy)] tracking-tight leading-tight group-hover:text-[var(--sci-teal)] transition-colors">
                  {j.title}
                </h3>
                <p className="mt-4 text-sm md:text-[15px] text-[var(--sci-text-muted)] leading-relaxed">
                  {j.description}
                </p>

                <div className="mt-6 flex flex-wrap gap-2">
                  {j.fields.map((f) => (
                    <span
                      key={f}
                      className="text-[11px] font-mono-sci uppercase tracking-[0.18em] text-[var(--sci-navy)] border border-[var(--sci-border)] px-2.5 py-1.5"
                    >
                      {f}
                    </span>
                  ))}
                </div>

                <div className="mt-8 pt-6 border-t border-[var(--sci-border)] flex items-center justify-between">
                  <span className="inline-flex items-center gap-2 text-sm font-medium text-[var(--sci-navy)] group-hover:text-[var(--sci-teal)] transition-colors">
                    Visit Journal
                    <ExternalLink size={14} />
                  </span>
                  <span className="font-mono-sci text-[11px] text-[var(--sci-text-muted)] tracking-wider">
                    {j.url.replace(/^https?:\/\/(www\.)?/, "")}
                  </span>
                </div>
              </div>
            </motion.a>
          ))}
        </div>
      </div>
    </section>
  );
}
